import { bool, func } from 'prop-types';
import React from 'react';
import { Col, FormGroup, Input, Label, Row } from 'reactstrap';

import Association from '../../associations/Association';
import DiscardableForm from '../../generic/DiscardableForm';
import SingleSelect from '../../generic/SingleSelect';
import TimestampInfo from '../../generic/TimestampInfo';
import { contactShape } from '../../prop-types';
import { alertSubscriptionStatuses, contactStatuses } from '../constants';

function ContactForm({ model, onChange, onSubmit, newModel, onDiscard }) {
  const {
    accountId,
    firstName,
    lastName,
    title,
    description,
    email,
    mobile,
    phone,
    fax,
    created,
    lastModified,
    createdBy,
    lastModifiedBy,
    brandIds,
    status,
    merchants,
    alertSubscriptionStatus,
    salesforceId,
    isZDirectUser,
  } = model;
  const inputProps = { onChange, readOnly: !onSubmit };
  const merchantIds = merchants ? merchants.map(x => x.id) : [];
  return (
    <DiscardableForm onSubmit={onSubmit} onDiscard={onDiscard}>
      <Row>
        <Col>
          <FormGroup row>
            <Label sm={4} for="co-firstName">
              First Name
            </Label>
            <Col sm={8}>
              <Input
                id="co-firstName"
                name="firstName"
                value={firstName || ''}
                {...inputProps}
              />
            </Col>
          </FormGroup>
          <FormGroup row>
            <Label sm={4} for="co-lastName">
              Last Name
            </Label>
            <Col sm={8}>
              <Input
                id="co-lastName"
                name="lastName"
                value={lastName || ''}
                {...inputProps}
              />
            </Col>
          </FormGroup>
          <FormGroup row>
            <Label sm={4} for="co-title">
              Title
            </Label>
            <Col sm={8}>
              <Input
                id="co-title"
                name="title"
                value={title || ''}
                {...inputProps}
              />
            </Col>
          </FormGroup>
          <FormGroup row>
            <Label sm={4} for="co-description">
              Description
            </Label>
            <Col sm={8}>
              <Input
                id="co-description"
                name="description"
                type="textarea"
                value={description || ''}
                {...inputProps}
              />
            </Col>
          </FormGroup>
        </Col>
        <Col>
          <FormGroup row>
            <Label sm={4} for="co-email" className="required">
              Email
            </Label>
            <Col sm={8}>
              <Input
                id="co-email"
                name="email"
                value={email || ''}
                {...inputProps}
              />
            </Col>
          </FormGroup>
          <FormGroup row>
            <Label sm={4} for="co-phone">
              Phone (landline)
            </Label>
            <Col sm={8}>
              <Input
                id="co-phone"
                name="phone"
                value={phone || ''}
                {...inputProps}
              />
            </Col>
          </FormGroup>
          <FormGroup row>
            <Label sm={4} for="co-mobile">
              Mobile
            </Label>
            <Col sm={8}>
              <Input
                id="co-mobile"
                name="mobile"
                value={mobile || ''}
                {...inputProps}
              />
            </Col>
          </FormGroup>
          <FormGroup row>
            <Label sm={4} for="co-fax">
              Fax
            </Label>
            <Col sm={8}>
              <Input id="co-fax" name="fax" value={fax || ''} {...inputProps} />
            </Col>
          </FormGroup>
          <FormGroup row>
            <Label sm={4} for="co-status">
              Status
            </Label>
            <Col sm={8}>
              <SingleSelect
                options={contactStatuses}
                id="co-status"
                name="status"
                value={status}
                {...inputProps}
              />
            </Col>
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col>
          <FormGroup row>
            <Label sm={4} for="co-accountId" className="required">
              Account
            </Label>
            <Col sm={8}>
              <Association
                id="co-accountId"
                name="accountId"
                value={accountId}
                type="account"
                {...inputProps}
                readOnly={inputProps.readOnly || !newModel}
              />
            </Col>
          </FormGroup>
          <FormGroup row>
            <Label sm={4} for="co-brandIds" className="required">
              Brands
            </Label>
            <Col sm={8}>
              <Association
                id="co-brandIds"
                name="brandIds"
                isMulti
                value={brandIds}
                type="brand"
                {...inputProps}
                readOnly
              />
            </Col>
          </FormGroup>
          <FormGroup row>
            <Label sm={4} for="co-merchantIds">
              Merchants
            </Label>
            <Col sm={8}>
              <Association
                id="co-merchantIds"
                name="merchantIds"
                value={merchantIds}
                type="zmsamMerchant"
                {...inputProps}
                isMulti
                readOnly
              />
            </Col>
          </FormGroup>
          <FormGroup row>
            <Label sm={4} for="co-alertSubscriptionStatus">
              Alert Notifications
            </Label>
            <Col sm={8}>
              <SingleSelect
                options={alertSubscriptionStatuses}
                id="co-alertSubscriptionStatus"
                name="alertSubscriptionStatus"
                value={alertSubscriptionStatus}
                {...inputProps}
              />
            </Col>
          </FormGroup>
          <FormGroup row>
            <Label sm={4} for="co-salesforceId">
              Salesforce ID
            </Label>
            <Col sm={8}>
              <Input
                id="co-salesforceId"
                name="salesforceId"
                value={salesforceId}
                {...inputProps}
                readOnly
              />
            </Col>
          </FormGroup>
          <FormGroup row>
            <Label sm={4} for="co-zDirectUser">
              zDirect User
            </Label>
            <Col sm={8}>
              <Input
                type="checkbox"
                id="co-zDirectUser"
                name="zDirectUser"
                checked={isZDirectUser}
                className="form-control form-checkbox"
                {...inputProps}
                disabled={!onSubmit}
                readOnly
              />
            </Col>
          </FormGroup>
        </Col>
        <Col>
          {!newModel && (
            <Row className="small">
              <Col sm="4" />
              <Col sm="8">
                <TimestampInfo
                  timestamp={created}
                  actionName="created"
                  author={createdBy}
                  displayAttribute="inline"
                />
                <TimestampInfo
                  timestamp={lastModified}
                  actionName="lastModified"
                  author={lastModifiedBy}
                  displayAttribute="inline"
                />
              </Col>
            </Row>
          )}
        </Col>
      </Row>
    </DiscardableForm>
  );
}

ContactForm.propTypes = {
  model: contactShape.isRequired,
  onChange: func,
  onSubmit: func,
  onDiscard: func,
  newModel: bool,
};

ContactForm.defaultProps = {
  onChange: undefined,
  onSubmit: undefined,
  onDiscard: undefined,
  newModel: false,
};

export default ContactForm;
